import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box, Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { shouldResetCountyField } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { entitySideDataTableStyles } from 'app/shared/componentsRestyled/common/form/shared/styles';
import { AddBoxIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import TableCDS from 'app/shared/componentsRestyled/coreDesignSystem/Table/TableCDS';
import _ from 'lodash';

import { IconSize } from 'constants/coreDesignSystem';
import { FeatureFlag } from 'constants/featureFlags';
import {
  EmploymentVerificationType,
  extendedIncomeTypes,
  IncomeType,
} from 'constants/loanApplication';
import { salaryMap } from 'constants/options';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePrevious from 'hooks/usePrevious';
import useSort from 'hooks/useSort';
import { getConditionalObject } from 'utils/data';
import { calculatePendingChanges } from 'utils/pendingChanges';

import { getLocationsCountiesAction } from 'store/loanApplications/actions';
import {
  selectFinancialInfoOwnersOptions,
  selectIsCountiesExist,
  selectSortedCountiesByStates,
} from 'store/loanApplications/selectors';

import { SectionContentPropTypes } from '../SectionContent.constants';
import { customOnAutocompleteChange } from '../SectionContent.utils';

import {
  getConfig,
  getEmploymentVerificationTableConfig,
  getSalaryTypeModel,
  notFoundTableData,
} from './EmploymentAndIncome.config';
import {
  defaultEmploymentVerificationSortOrder,
  PREVIOUS_EMPLOYMENT_AMOUNT_INPUT_PROPS,
  SCROLL_DELAY_MS,
} from './EmploymentAndIncome.constants';
import { addVerificationButtonsRowStyles, tableWrapperStyle } from './EmploymentAndIncome.styles';
import { EmploymentAndIncomeTestIds } from './EmploymentAndIncome.testIds';
import {
  createNewEmploymentVerificationData,
  normalizeEmploymentVerificationFormData,
  orderEmploymentVerification,
  prepareInitialDeletedFlags,
  prepareModifiedFields,
  salaryIndexedMap,
} from './EmploymentAndIncome.utils';
import EmploymentVerificationForm from './EmploymentVerificationForm';

const defaultEmploymentVerification = [];

const EmploymentAndIncome = ({
  pendingChanges,
  form,
  handleChange,
  isLoading,
  setInForm,
  setForm,
  validateField,
  validationErrors,
  isSaving,
  recordId,
  setRecordConfig,
  initialFormValues,
  data,
  setIsLoading,
  isDeleted,
  isNewRecord,
  borrowerId,
  staticValidateField,
  disabledPendingChangesHighlight,
  formDataRef,
  setEntitiesSideData,
  entitiesSideData,
  initialEntitiesSideData,
  setIsSavingAllowed,
  setInitialEntitiesSideData,
  setIsNestedEntityEditActive,
}) => {
  const dispatch = useDispatch();
  const [visibleSalaryFields, setVisibleSalaryFields] = useState({});
  const [busySalaryTypes, setBusySalaryTypes] = useState({});
  const [editableEmploymentVerificationData, setEditableEmploymentVerificationData] =
    useState(null);

  const { order, onRequestSort } = useSort(defaultEmploymentVerificationSortOrder);

  const isMilitaryAddressAllowed = useFeatureToggle(FeatureFlag.MilitaryAddressToStates);
  const counties = useSelector(selectSortedCountiesByStates);
  const isCountiesExist = useSelector(selectIsCountiesExist);
  const ownersOptions = useSelector(selectFinancialInfoOwnersOptions);

  const initialFormValuesRef = useRef({});

  const recordForm = form[recordId];
  const incomeType = recordForm?.incomeType?.value;
  const isW2IncomeType = incomeType === IncomeType.W2;
  const isSelfEmployedIncomeType = incomeType === IncomeType.SELF_EMPLOYED;
  const isCurrentlyEmployedHere = recordForm?.isCurrentlyEmployedHere;
  const endDate = recordForm?.endDate;
  const isEmploymentOwnershipType = recordForm?.employmentOwnershipType;
  const taxFilingType = recordForm?.taxFilingType?.value;
  const isPreviousWorkAddressExist = recordForm?.isPreviousWorkAddressExist;
  const workAddressState = recordForm?.workAddress?.state?.value;
  const previousWorkAddressState = recordForm?.previousWorkAddress?.state?.value;
  const yearsInProfession = recordForm?.yearsInProfession;
  const monthsInProfession = recordForm?.monthsInProfession;
  const isYearsInProfessionRequired = ~~monthsInProfession === 0 && ~~yearsInProfession === 0;
  const isMonthsInProfessionRequired = ~~yearsInProfession === 0;
  const isPreviousEmployment = isCurrentlyEmployedHere === false && !!endDate;
  const previousEmploymentAmountProps = isPreviousEmployment
    ? PREVIOUS_EMPLOYMENT_AMOUNT_INPUT_PROPS
    : null;
  const entityData = data[recordId];
  const employmentVerificationTableData = entityData?.employmentVerifications;

  const entitySideData = entitiesSideData[recordId];
  const { employmentVerifications } = entitySideData || {};
  const isEmploymentVerificationEditActive = !!editableEmploymentVerificationData;
  const isEmploymentVerificationExist = !!employmentVerifications?.length;

  const initialEmploymentVerificationValuesMap = useMemo(() => {
    if (!initialEntitiesSideData[recordId]?.employmentVerifications) {
      return {};
    }

    return _.keyBy(initialEntitiesSideData[recordId].employmentVerifications, 'id');
  }, [initialEntitiesSideData, recordId]);

  const employmentVerificationPendingChangesData = useMemo(
    () => pendingChanges?.[recordId]?.employmentVerifications ?? {},
    [pendingChanges, recordId],
  );

  const pendingChangesCount = calculatePendingChanges(
    employmentVerificationPendingChangesData,
  ).count;

  const orderedEmploymentVerificationWithPendingChanges = useMemo(() => {
    if (!isEmploymentVerificationExist) {
      return defaultEmploymentVerification;
    }

    const orderedEmploymentVerification = orderEmploymentVerification({
      employmentVerifications,
      order,
    });

    return prepareModifiedFields({
      currentData: orderedEmploymentVerification,
      pendingChangesData: employmentVerificationPendingChangesData,
      initialValuesMap: initialEmploymentVerificationValuesMap,
    });
  }, [
    isEmploymentVerificationExist,
    employmentVerifications,
    order,
    initialEmploymentVerificationValuesMap,
    employmentVerificationPendingChangesData,
  ]);

  const correctedEmploymentVerificationTableData = isEmploymentVerificationExist
    ? orderedEmploymentVerificationWithPendingChanges
    : notFoundTableData;

  useEffect(() => {
    setEditableEmploymentVerificationData(null);
  }, [recordId]);

  useEffect(() => {
    setIsSavingAllowed(!isEmploymentVerificationEditActive);
    setIsNestedEntityEditActive(isEmploymentVerificationEditActive);

    return () => {
      setIsSavingAllowed(true);
    };
  }, [setIsSavingAllowed, isEmploymentVerificationEditActive, setIsNestedEntityEditActive]);

  useEffect(() => {
    if (!employmentVerificationTableData || !(isW2IncomeType || isSelfEmployedIncomeType)) {
      return;
    }

    const entitySideData = initialEntitiesSideData[recordId] || {};
    const isEmploymentVerificationExist = _.has(entitySideData, 'employmentVerifications');

    if (isEmploymentVerificationExist) {
      return;
    }

    const preparedInitialData = prepareInitialDeletedFlags({
      employmentVerifications: employmentVerificationTableData,
      employmentVerificationPendingChangesData,
      isRecordDeleted: isDeleted,
      isRecordNewAdded: isNewRecord,
    });

    setEntitiesSideData(prevState => ({
      ...prevState,
      [recordId]: {
        ...entitySideData,
        employmentVerifications: preparedInitialData,
      },
    }));
    setInitialEntitiesSideData(prevState => ({
      ...prevState,
      [recordId]: {
        ...entitySideData,
        employmentVerifications: preparedInitialData,
      },
    }));
  }, [
    isW2IncomeType,
    isSelfEmployedIncomeType,
    employmentVerificationPendingChangesData,
    employmentVerificationTableData,
    initialEntitiesSideData,
    isDeleted,
    isNewRecord,
    recordId,
    setEntitiesSideData,
    setInitialEntitiesSideData,
  ]);

  const onChangeExperienceField = useCallback(
    (e, relatedFieldNameInModel) => {
      handleChange(e);

      setTimeout(() => {
        const relatedFieldValue = _.get(formDataRef.current, relatedFieldNameInModel);

        staticValidateField(relatedFieldNameInModel, relatedFieldValue, formDataRef.current);
      }, SCROLL_DELAY_MS);
    },
    [handleChange, staticValidateField, formDataRef],
  );

  const onChangeSalaryType = useCallback(
    (e, value, salaryTypeModel) => {
      const customAction = ({ fieldValue }) =>
        setBusySalaryTypes(prev => ({
          ...prev,
          [recordId]: {
            ...prev[recordId],
            [salaryTypeModel]: fieldValue.value,
          },
        }));

      customOnAutocompleteChange({ e, value, customAction, validateField, setInForm });
    },
    [validateField, recordId, setInForm],
  );

  const onAddSalaryType = useCallback(
    index => {
      const closestNotBusySalaryOption = Array.from(salaryIndexedMap).filter(
        ([option]) =>
          !Object.values(busySalaryTypes[recordId] ?? {}).find(value => value === option),
      )[0][0];

      setVisibleSalaryFields(prev => {
        const prevVisibleFieldsEntries = Object.entries(prev[recordId] ?? {});

        if (index === -1 && prevVisibleFieldsEntries.length) {
          return prev;
        }

        const newSalaryTypeIndex = prevVisibleFieldsEntries.length
          ? Math.max(...prevVisibleFieldsEntries.map(([__, { index }]) => index)) + 1
          : 0;
        const nameInModel = `${recordId}.salary.${getSalaryTypeModel(newSalaryTypeIndex)}.input`;

        if (!closestNotBusySalaryOption) {
          return prev;
        }

        setInForm(nameInModel, salaryMap[closestNotBusySalaryOption]);

        const newSalaryTypeModel = getSalaryTypeModel(newSalaryTypeIndex);
        const prevRecordEntries = Object.entries(prev[recordId] ?? {});

        setBusySalaryTypes(prev => ({
          ...prev,
          [recordId]: {
            ...prev[recordId],
            [newSalaryTypeModel]: closestNotBusySalaryOption,
          },
        }));

        return {
          ...prev,
          [recordId]: Object.fromEntries([
            ...prevRecordEntries.slice(0, index + 1),
            [
              [newSalaryTypeModel],
              {
                input: closestNotBusySalaryOption,
                value: null,
                index: newSalaryTypeIndex,
              },
            ],
            ...prevRecordEntries.slice(index + 1),
          ]),
        };
      });
    },
    [setInForm, recordId, busySalaryTypes],
  );

  const onRemoveSalaryType = useCallback(
    salaryTypeModel => {
      setVisibleSalaryFields(prev => {
        const { [salaryTypeModel]: removedVisibleSalaryField, ...restVisibleSalaryFields } =
          prev[recordId];

        return {
          ...prev,
          [recordId]: {
            ...restVisibleSalaryFields,
          },
        };
      });

      setBusySalaryTypes(prev => {
        const { [salaryTypeModel]: removedBusySalaryType, ...restBusySalaryTypes } =
          prev[recordId] ?? {};

        return {
          ...prev,
          [recordId]: {
            ...restBusySalaryTypes,
          },
        };
      });

      setForm(prev => {
        const { [salaryTypeModel]: removedSalaryType, ...restSalaryTypes } =
          prev[recordId]?.salary ?? {};

        return {
          ...prev,
          [recordId]: {
            ...prev[recordId],
            salary: {
              ...restSalaryTypes,
            },
          },
        };
      });
    },

    [setForm, recordId],
  );

  const onChangeState = useCallback(
    (e, value, addressName) => {
      const customAction = ({ fieldValue }) =>
        setForm(prev => {
          const shouldResetCounty = shouldResetCountyField({
            counties,
            formState: fieldValue.value,
            formCounty: prev[recordId]?.[addressName]?.county?.value,
          });

          if (!shouldResetCounty) {
            return prev;
          }

          return {
            ...prev,
            [recordId]: {
              ...prev[recordId],
              [addressName]: {
                ...prev[recordId]?.[addressName],
                county: null,
              },
            },
          };
        });

      customOnAutocompleteChange({ e, value, customAction, validateField, setInForm });
    },
    [validateField, recordId, setForm, counties, setInForm],
  );

  const onChangeTaxFillingType = useCallback(
    (e, value) => {
      const customAction = () =>
        setForm(prev => ({
          ...prev,
          [recordId]: {
            ...prev[recordId],
            titlePerTaxReturn: { name: '', value: null },
          },
        }));
      customOnAutocompleteChange({ e, value, customAction, validateField, setInForm });
    },
    [recordId, setForm, validateField, setInForm],
  );

  const deleteEmploymentVerification = useCallback(
    id => {
      setEntitiesSideData(prevState => ({
        ...prevState,
        [recordId]: {
          ...prevState[recordId],
          employmentVerifications: prevState[recordId].employmentVerifications.reduce(
            (acc, employmentVerification) => {
              if (employmentVerification.id !== id) {
                acc.push(employmentVerification);

                return acc;
              }

              if (employmentVerification.isAdded) {
                return acc;
              }

              acc.push({ ...employmentVerification, isDeleted: true });

              return acc;
            },
            [],
          ),
        },
      }));
      setEditableEmploymentVerificationData(null);
    },
    [recordId, setEntitiesSideData],
  );

  const addEmploymentVerificationOfType = useCallback(
    verificationType => {
      const employmentVerificationData = createNewEmploymentVerificationData({ verificationType });

      setEntitiesSideData(prevState => ({
        ...prevState,
        [recordId]: {
          ...prevState[recordId],
          employmentVerifications: [
            ...(prevState[recordId]?.employmentVerifications ?? []),
            employmentVerificationData,
          ],
        },
      }));
      setEditableEmploymentVerificationData(employmentVerificationData);
    },
    [recordId, setEntitiesSideData, setEditableEmploymentVerificationData],
  );

  const addNewEmploymentVerification = useCallback(
    () => addEmploymentVerificationOfType(EmploymentVerificationType.STANDARD),
    [addEmploymentVerificationOfType],
  );

  const addNewFundingVerification = useCallback(
    () => addEmploymentVerificationOfType(EmploymentVerificationType.FUNDING),
    [addEmploymentVerificationOfType],
  );

  const employmentVerificationTableConfig = useMemo(
    () =>
      getEmploymentVerificationTableConfig({
        setEditableEmploymentVerificationData,
        deleteEmploymentVerification,
        editableRowId: editableEmploymentVerificationData?.id,
        isEmploymentVerificationActionDisabled: isLoading,
      }),
    [deleteEmploymentVerification, editableEmploymentVerificationData?.id, isLoading],
  );

  const config = useMemo(
    () =>
      getConfig({
        recordId,
        incomeType,
        isCurrentlyEmployedHere,
        previousEmploymentAmountProps,
        visibleSalaryFields: visibleSalaryFields[recordId] ?? {},
        onAddSalaryType,
        onRemoveSalaryType,
        onChangeSalaryType,
        onChangeState,
        onChangeTaxFillingType,
        onChangeExperienceField,
        busySalaryTypes: busySalaryTypes[recordId] ?? {},
        isMilitaryAddressAllowed,
        counties,
        workAddressState,
        previousWorkAddressState,
        isPreviousWorkAddressExist,
        isEmploymentOwnershipType,
        taxFilingType,
        isYearsInProfessionRequired,
        isMonthsInProfessionRequired,
        isNewRecord,
        ownersOptions,
        isLoading,
      }),
    [
      recordId,
      incomeType,
      isCurrentlyEmployedHere,
      previousEmploymentAmountProps,
      visibleSalaryFields,
      onAddSalaryType,
      onRemoveSalaryType,
      onChangeSalaryType,
      onChangeState,
      onChangeTaxFillingType,
      onChangeExperienceField,
      busySalaryTypes,
      isMilitaryAddressAllowed,
      counties,
      workAddressState,
      previousWorkAddressState,
      isPreviousWorkAddressExist,
      isEmploymentOwnershipType,
      taxFilingType,
      isYearsInProfessionRequired,
      isMonthsInProfessionRequired,
      isNewRecord,
      ownersOptions,
      isLoading,
    ],
  );

  useEffect(() => {
    if (isDeleted) {
      return;
    }

    setRecordConfig(config, recordId);
  }, [config, setRecordConfig, isDeleted, recordId]);

  const prevIncomeType = usePrevious(incomeType);

  useEffect(() => {
    if (prevIncomeType === IncomeType.W2 && incomeType !== prevIncomeType) {
      setVisibleSalaryFields(prev => ({
        ...prev,
        [recordId]: {},
      }));

      setBusySalaryTypes(prev => ({
        ...prev,
        [recordId]: {},
      }));
    }

    const initialSalaryFieldsEntries = Object.entries(data[recordId]?.salary ?? {}).filter(
      ([__, { value }]) => !_.isNil(value),
    );

    const getRecordData = (existsData, initialData) =>
      Object.values(existsData ?? {}).length ? existsData : initialData;

    setVisibleSalaryFields(prev => ({
      ...prev,
      [recordId]: getRecordData(prev[recordId], Object.fromEntries(initialSalaryFieldsEntries)),
    }));

    setBusySalaryTypes(prev => ({
      ...prev,
      [recordId]: getRecordData(
        prev[recordId],
        Object.fromEntries(initialSalaryFieldsEntries.map(([key, { input }]) => [key, input])),
      ),
    }));
  }, [data, recordId, incomeType, prevIncomeType]);

  useEffect(() => {
    setForm(prev => {
      const {
        incomeType: incomeTypeForm,
        startDate,
        monthlyIncome,
        endDate,
        salary,
        grossMonthlyBaseSalary,
        companyEin,
        taxFilingType,
        titlePerTaxReturn,
        isPreviousWorkAddressExist,
        previousWorkAddress,
        isEmployedByPartyToTransaction,
        owner,
        ...restRecordData
      } = prev[recordId] ?? {};

      const initialSalaryFields = data[recordId]?.initialSalaryFields;

      if (initialSalaryFields && salary) {
        setInForm(
          `[${recordId}].isSalaryChanged`,
          !!initialSalaryFields && initialSalaryFields !== _.size(salary ?? {}),
        );
      }

      return {
        ...prev,
        [recordId]: {
          incomeType: incomeTypeForm,
          startDate,
          ...getConditionalObject(isNewRecord, () => ({
            owner: owner ?? ownersOptions.find(({ value }) => value === borrowerId),
          })),
          ...getConditionalObject(!extendedIncomeTypes.includes(incomeType), {
            monthlyIncome,
          }),
          ...getConditionalObject(incomeType === IncomeType.SELF_EMPLOYED, {
            grossMonthlyBaseSalary,
          }),
          ...getConditionalObject(extendedIncomeTypes.includes(incomeType), () => ({
            ...getConditionalObject(!_.isNil(endDate), { endDate }),
            ...getConditionalObject(incomeType === IncomeType.W2, {
              salary,
              isEmployedByPartyToTransaction,
            }),
            ...getConditionalObject(incomeType === IncomeType.SELF_EMPLOYED, () => ({
              ...getConditionalObject(isEmploymentOwnershipType, () => ({
                companyEin,
                taxFilingType,
                titlePerTaxReturn,
                isPreviousWorkAddressExist,
                ...getConditionalObject(isPreviousWorkAddressExist, {
                  previousWorkAddress,
                }),
              })),
            })),
            ...restRecordData,
          })),
        },
      };
    });
  }, [
    data,
    setForm,
    recordId,
    initialFormValues,
    incomeType,
    isEmploymentOwnershipType,
    setInForm,
    isNewRecord,
    borrowerId,
    ownersOptions,
  ]);

  useEffect(() => {
    if (incomeType !== IncomeType.W2 || Object.values(visibleSalaryFields[recordId] ?? {}).length) {
      return;
    }

    onAddSalaryType(-1);
  }, [incomeType, visibleSalaryFields, recordId, onAddSalaryType]);

  useEffect(() => {
    initialFormValuesRef.current = initialFormValues;
  }, [initialFormValues]);

  useEffectSafeStrictMode(() => {
    if (isCountiesExist) {
      return;
    }

    setIsLoading(true);

    dispatch(getLocationsCountiesAction())
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, isCountiesExist, setIsLoading]);

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        setEntitiesSideData(prevState => ({
          ...prevState,
          [recordId]: {
            ...prevState[recordId],
            employmentVerifications: prevState[recordId].employmentVerifications.filter(
              ({ id }) => id !== data.id,
            ),
          },
        }));
      }

      setEditableEmploymentVerificationData(null);
    },
    [recordId, setEntitiesSideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const dataToSave = normalizeEmploymentVerificationFormData({ updatedData, initialData });

      setEntitiesSideData(prevState => ({
        ...prevState,
        [recordId]: {
          ...prevState[recordId],
          employmentVerifications: prevState[recordId].employmentVerifications.map(
            employmentVerification =>
              employmentVerification.id === dataToSave.id ? dataToSave : employmentVerification,
          ),
        },
      }));
      setEditableEmploymentVerificationData(null);
    },
    [recordId, setEntitiesSideData],
  );

  const additionalRowContent = editableEmploymentVerificationData ? (
    <EmploymentVerificationForm
      cancelForm={cancelForm}
      employmentVerificationData={editableEmploymentVerificationData}
      isLoading={isLoading}
      isSelfEmployedIncomeType={isSelfEmployedIncomeType}
      saveForm={saveForm}
    />
  ) : null;

  return (
    <>
      <FormGenerator
        isNestedForm
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.EMPLOYMENT_INCOME}
        handleChange={handleChange}
        initialFormValues={initialFormValues}
        isLoading={isSaving || isLoading}
        numberOfColumns={4}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        staticValidateField={staticValidateField}
        validateField={validateField}
        validationErrors={validationErrors}
      />

      {(isW2IncomeType || isSelfEmployedIncomeType) && (
        <>
          <TableCDS
            isHideTableWhenEmpty
            additionalRowContent={additionalRowContent}
            additionalRowContentRowId={editableEmploymentVerificationData?.id}
            config={employmentVerificationTableConfig}
            customStyles={entitySideDataTableStyles}
            data={correctedEmploymentVerificationTableData}
            isCustomDataDefinition={!isEmploymentVerificationExist}
            isLoading={isLoading && !isSaving}
            isSortDisabled={isEmploymentVerificationEditActive}
            order={order}
            pendingChangesCount={
              isNewRecord || isDeleted || disabledPendingChangesHighlight ? 0 : pendingChangesCount
            }
            title="Employment Verification"
            wrapperStyle={tableWrapperStyle}
            onRequestSort={onRequestSort}
          />

          <Box sx={addVerificationButtonsRowStyles}>
            <Button
              data-testid={EmploymentAndIncomeTestIds.ADD_EMPLOYMENT_VERIFICATION_BUTTON}
              disabled={isEmploymentVerificationEditActive || isLoading}
              size="medium"
              startIcon={<AddBoxIcon size={IconSize[20]} />}
              variant="outlined"
              onClick={addNewEmploymentVerification}
            >
              Add Employment Verification
            </Button>
            <Button
              data-testid={EmploymentAndIncomeTestIds.ADD_FUNDING_VERIFICATION_BUTTON}
              disabled={isEmploymentVerificationEditActive || isLoading}
              size="medium"
              startIcon={<AddBoxIcon size={IconSize[20]} />}
              variant="outlined"
              onClick={addNewFundingVerification}
            >
              Add Funding Verification
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

EmploymentAndIncome.propTypes = {
  ...SectionContentPropTypes,
};

export default memo(EmploymentAndIncome);
